import clsx from 'clsx'
import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * The way into the rest of the workspace.
 *
 * Each tile is a whole link — not a card with a link in it — so the pointer target, the
 * keyboard target and the thing you can middle-click into a new tab are all the same object.
 *
 * **A count is `null` until its endpoint has answered, and a null count renders nothing.** No
 * zero, no em dash, no skeleton digit. A tile that shows "0 documents" while the request is
 * still in flight has told the reader something false about their own workspace, and the one
 * thing this product sells is that the numbers on screen were measured rather than assumed.
 *
 * The wash is `.accent-wash`, which mixes the tone into whichever theme is active instead of
 * painting a fixed tint over it — the same reason an agent's card carries its accent that
 * way. Tones are the three semantic channels and nothing else: the model, a resolved source,
 * a consequence. The reference design has a fourth colour for Activity; there is no fourth
 * channel in the palette, and inventing one would give a hue a meaning nothing else honours.
 */

export type HomeTileTone = 'primary' | 'grounded' | 'alert'

export type HomeTile = {
  to: string
  icon: ReactNode
  title: string
  body: string
  /**
   * Null while the endpoint behind it has not answered. Never a placeholder number.
   *
   * Already formatted by the page, which holds the locale: a document total runs into the
   * thousands, and a bare number renders "12345" to everyone instead of "12,345" and
   * "12.345" to the readers who expect them (PRD-27 F-2).
   */
  count: string | null
  /**
   * Rendered immediately after the count, for a number the API can only floor. `50+` is the
   * honest reading of a page-capped list; a bare `50` claims a total the endpoint never gave.
   */
  countSuffix?: string | undefined
  tone: HomeTileTone
}

const TONES: Record<HomeTileTone, { text: string; wash: string }> = {
  primary: { text: 'text-primary', wash: 'var(--primary)' },
  grounded: { text: 'text-grounded', wash: 'var(--grounded)' },
  alert: { text: 'text-alert', wash: 'var(--alert)' }
}

/** One tile's share of the 936px four-up row in the reference design. */
const TILE_WIDTH = 225
/** Matches `gap-3` below; both feed the same measurement. */
const GAP = 12

export const HomeTiles = ({ tiles }: { tiles: HomeTile[] }) => (
  <div
    className="mt-8 grid w-full gap-3"
    style={{
      // Auto-fit against a max width sized to the number of tiles: at full width the row is
      // exactly as wide as its contents (three tiles are not stretched across four columns,
      // and a fifth does not strand itself on a second row), and it steps down to two and
      // then one column on the way to a phone without a breakpoint per case.
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      maxWidth: tiles.length * TILE_WIDTH + (tiles.length - 1) * GAP
    }}
  >
    {tiles.map((tile, index) => {
      const tone = TONES[tile.tone]
      return (
        /*
          The entrance and the hover live on different elements on purpose. `.rise` fills both
          ways, and a filled animation keeps hold of `transform` after it ends — on the same
          element it would quietly cancel the hover lift, leaving a card that looks interactive
          and does not move.
        */
        <div key={tile.to} className="rise" style={{ animationDelay: `${220 + index * 70}ms` }}>
          <Link
            to={tile.to}
            className="accent-wash card-hover block h-full rounded-card border border-rule px-[15px] pb-[15px] pt-[14px] text-fg shadow-glass"
            style={{ '--wash-accent': tone.wash } as CSSProperties}
          >
            <span className="flex items-center gap-[9px]">
              <span
                className={clsx(
                  'flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[11px]',
                  'border border-rule bg-card-solid shadow-glass',
                  tone.text
                )}
              >
                {tile.icon}
              </span>
              {tile.count !== null && (
                <span className={clsx('readout ml-auto text-[15px] font-medium tracking-[-0.02em]', tone.text)}>
                  {tile.count}
                  {tile.countSuffix}
                </span>
              )}
            </span>
            <span className="mt-5 block text-[14px] font-semibold tracking-[-0.02em]">{tile.title}</span>
            <span className="mt-1 block text-pretty text-[11.5px] leading-[1.5] text-fg-muted">{tile.body}</span>
          </Link>
        </div>
      )
    })}
  </div>
)
